import React from 'react';
import { COLORS } from '../../utils/constants';

const DataInsights = ({ variant = 'signin' }) => {
  const isSignIn = variant === 'signin';

  return (
    <div style={{
      width: '100%',
      height: '100%',
      background: `linear-gradient(135deg, ${COLORS.blue} 0%, #0052CC 100%)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      position: 'relative'
    }}>
      <style>{`
        @keyframes fadeInUp {
          0% { transform: translateY(30px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.05); opacity: 0.8; }
        }
        @keyframes draw {
          from { stroke-dashoffset: 300; }
          to { stroke-dashoffset: 0; }
        }
        @keyframes countUp {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .fade-1 { animation: fadeInUp 0.8s ease-out 0.2s both; }
        .fade-2 { animation: fadeInUp 0.8s ease-out 0.4s both; }
        .fade-3 { animation: fadeInUp 0.8s ease-out 0.6s both; }
        .fade-4 { animation: fadeInUp 0.8s ease-out 0.8s both; }
        .float { animation: float 4s ease-in-out infinite; }
        .float-delayed { animation: float 4s ease-in-out 1s infinite; }
        .pulse { animation: pulse 3s ease-in-out infinite; }
        .draw-line {
          stroke-dasharray: 300;
          stroke-dashoffset: 300;
          animation: draw 2s ease-out 0.5s forwards;
        }
        .count-up { animation: countUp 0.6s ease-out 1s both; }
      `}</style>

      {/* Background decorative elements */}
      <div style={{
        position: 'absolute',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.05)',
        top: '-100px',
        right: '-100px'
      }} />
      <div style={{
        position: 'absolute',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'rgba(0, 255, 198, 0.08)',
        bottom: '-80px',
        left: '-80px'
      }} />

      {/* Main SVG illustration */}
      <svg width="600" height="500" viewBox="0 0 600 500" style={{ maxWidth: '95%', height: 'auto' }}>
        <defs>
          <linearGradient id="chartGradient" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" style={{ stopColor: COLORS.mint, stopOpacity: 0.2 }} />
            <stop offset="100%" style={{ stopColor: COLORS.mint, stopOpacity: 0.8 }} />
          </linearGradient>
          <linearGradient id="barGradient" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" style={{ stopColor: COLORS.mint, stopOpacity: 0.6 }} />
            <stop offset="100%" style={{ stopColor: COLORS.mint, stopOpacity: 1 }} />
          </linearGradient>
          <linearGradient id="cardGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: 'white', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#f0f9ff', stopOpacity: 1 }} />
          </linearGradient>
        </defs>

        {/* Main dashboard card */}
        <g className="fade-1">
          <rect x="80" y="60" width="440" height="320" rx="20" fill="url(#cardGradient)" />

          {/* Card header */}
          <rect x="100" y="80" width="400" height="50" rx="10" fill={COLORS.blue} opacity="0.08" />
          <circle cx="125" cy="105" r="8" fill={COLORS.mint} />
          <rect x="145" y="98" width="120" height="14" rx="7" fill={COLORS.blue} opacity="0.15" />

          {/* Three dots */}
          <circle cx="445" cy="105" r="4" fill={COLORS.blue} opacity="0.3" />
          <circle cx="460" cy="105" r="4" fill={COLORS.blue} opacity="0.3" />
          <circle cx="475" cy="105" r="4" fill={COLORS.blue} opacity="0.3" />
        </g>

        {/* Analytics chart area */}
        <g className="fade-2 float">
          {/* Chart background grid lines */}
          <line x1="120" y1="160" x2="120" y2="340" stroke={COLORS.blue} strokeWidth="1" opacity="0.1" />
          <line x1="120" y1="340" x2="480" y2="340" stroke={COLORS.blue} strokeWidth="1" opacity="0.1" />

          {/* Horizontal grid lines */}
          <line x1="120" y1="190" x2="480" y2="190" stroke={COLORS.blue} strokeWidth="1" opacity="0.05" />
          <line x1="120" y1="220" x2="480" y2="220" stroke={COLORS.blue} strokeWidth="1" opacity="0.05" />
          <line x1="120" y1="250" x2="480" y2="250" stroke={COLORS.blue} strokeWidth="1" opacity="0.05" />
          <line x1="120" y1="280" x2="480" y2="280" stroke={COLORS.blue} strokeWidth="1" opacity="0.05" />
          <line x1="120" y1="310" x2="480" y2="310" stroke={COLORS.blue} strokeWidth="1" opacity="0.05" />

          {/* Bar chart */}
          <rect x="150" y="280" width="35" height="60" rx="4" fill="url(#barGradient)" className="fade-2" />
          <rect x="210" y="240" width="35" height="100" rx="4" fill="url(#barGradient)" className="fade-3" />
          <rect x="270" y="200" width="35" height="140" rx="4" fill="url(#barGradient)" className="fade-4" />
          <rect x="330" y="220" width="35" height="120" rx="4" fill="url(#barGradient)" className="fade-3" />
          <rect x="390" y="180" width="35" height="160" rx="4" fill="url(#barGradient)" className="fade-4" />
          <rect x="450" y="160" width="35" height="180" rx="4" fill="url(#barGradient)" className="fade-4" />

          {/* Trend line */}
          <path
            d="M 167 270 Q 227 230 287 190 Q 347 210 407 170 Q 437 150 467 150"
            fill="none"
            stroke={COLORS.mint}
            strokeWidth="3"
            strokeLinecap="round"
            className="draw-line"
          />

          {/* Data points on line */}
          <circle cx="167" cy="270" r="5" fill="white" stroke={COLORS.mint} strokeWidth="2" />
          <circle cx="287" cy="190" r="5" fill="white" stroke={COLORS.mint} strokeWidth="2" />
          <circle cx="407" cy="170" r="5" fill="white" stroke={COLORS.mint} strokeWidth="2" />
          <circle cx="467" cy="150" r="5" fill="white" stroke={COLORS.mint} strokeWidth="2" />
        </g>

        {/* Floating stat cards */}
        <g className="fade-3 float-delayed">
          {/* Response rate card */}
          <rect x="40" y="380" width="140" height="80" rx="12" fill="white" opacity="0.95" />
          <text x="60" y="410" fill={COLORS.gray} fontSize="11" fontWeight="500">Response Rate</text>
          <text x="60" y="440" fill={COLORS.blue} fontSize="24" fontWeight="700" className="count-up">94.2%</text>
          <circle cx="155" cy="420" r="18" fill={COLORS.mint} opacity="0.15" />
          <path d="M 148 420 L 152 424 L 162 414" stroke={COLORS.mint} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </g>

        <g className="fade-4 float">
          {/* Total responses card */}
          <rect x="420" y="380" width="140" height="80" rx="12" fill="white" opacity="0.95" />
          <text x="440" y="410" fill={COLORS.gray} fontSize="11" fontWeight="500">Total Responses</text>
          <text x="440" y="440" fill={COLORS.blue} fontSize="24" fontWeight="700" className="count-up">12,847</text>
          <circle cx="535" cy="420" r="18" fill={COLORS.blue} opacity="0.1" />
          <path d="M 528 426 L 535 420 L 542 426 M 535 420 L 535 432" stroke={COLORS.blue} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.6" />
        </g>

        {/* Decorative elements */}
        <g className="pulse">
          <circle cx="520" cy="100" r="25" fill="white" opacity="0.1" />
          <circle cx="520" cy="100" r="15" fill={COLORS.mint} opacity="0.3" />
        </g>

        <g className="pulse" style={{ animationDelay: '1s' }}>
          <circle cx="100" cy="420" r="20" fill="white" opacity="0.08" />
        </g>

        {/* Success indicator */}
        <g className="fade-4">
          <circle cx="300" cy="420" r="28" fill={COLORS.mint} />
          <path d="M 288 420 L 296 428 L 314 410" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>

        {/* Bottom text */}
        <text x="300" y="485" textAnchor="middle" fill="white" fontSize="22" fontWeight="700" opacity="0.95">
          {isSignIn ? 'Turn Responses into Insights' : 'Start Collecting Better Data'}
        </text>
      </svg>
    </div>
  );
};

export default DataInsights;
